import { action, select, thunk } from 'easy-peasy'
import {
  get as getCookie,
  set as setCookie,
  remove as removeCookie,
} from 'es-cookie'

const ENDPOINT = `${process.env.GATSBY_NETLIFY_URL}/.netlify/identity`

export const authModel = {
  auth: {
    token: null,
    authStage: 'INIT',

    // DERIVED STATE
    isLoggedIn: select(state => !!state.token),

    // ACTIONS
    setJwt: action((state, payload) => {
      setCookie('wtwJWT', payload)

      return {
        ...state,
        token: payload,
      }
    }),

    clearUser: action(state => {
      removeCookie('wtwJWT')

      return {
        ...state,
        user: null,
        token: null,
        authStage: 'SIGNED_OUT',
      }
    }),

    setAuthStage: action((state, payload) => ({
      ...state,
      authStage: payload,
    })),

    // THUNKS
    signUpUser: thunk(async (actions, payload) => {
      const request = await fetch(`${ENDPOINT}/verify`, {
        method: 'post',
        body: JSON.stringify({
          type: 'signup',
          password: payload.password,
          token: payload.inviteToken,
        }),
      })

      if (request.status !== 200) throw new Error('Registering user failed.')

      // We now have the user's JWT, so set it.
      const { access_token: jwt } = await request.json()
      actions.setJwt(jwt)
    }),

    sendRecoveryEmail: thunk(async (_, payload) => {
      const request = await fetch(`${ENDPOINT}/recover`, {
        method: 'post',
        body: JSON.stringify({
          email: payload,
        }),
      })

      if (request.status !== 200)
        throw new Error('Sending recovery email failed.')
    }),

    loginUser: thunk(async (actions, payload) => {
      const token = getCookie('wtwJWT')

      // There's no token, so we need to get one.
      if (!token) {
        const formData = new URLSearchParams()
        formData.append('grant_type', 'password')
        formData.append('username', payload.email)
        formData.append('password', payload.password)

        const request = await fetch(`${ENDPOINT}/token`, {
          method: 'post',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: formData.toString(),
        })

        if (request.status !== 200) throw new Error('Login failed')

        const { access_token: jwt } = await request.json()
        actions.setJwt(jwt)
        actions.setAuthStage('SIGNED_IN')
      } else {
        // There is a token, and we need to verify it.
        actions.setAuthStage('VERIFYING_JWT')
        const verifyRequest = await fetch(`${ENDPOINT}/user`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })

        // The token is bad or stale, so just show the login page.
        if (verifyRequest.status !== 200) {
          actions.clearUser()
          return
        }

        actions.setAuthStage('SIGNED_IN')
      }
    }),

    recoverUser: thunk(async (actions, payload) => {
      const request = await fetch(`${ENDPOINT}/verify`, {
        method: 'post',
        body: JSON.stringify({
          type: 'recovery',
          token: payload.recoveryToken,
        }),
      })
      if (request.status !== 200) throw new Error('Recovering user failed.')

      // We now have the user's JWT, so set it.
      const { access_token: jwt } = await request.json()
      actions.setJwt(jwt)

      // Now that we have the JWT, we can change the user's password for them.
      const resetRequest = await fetch(`${ENDPOINT}/user`, {
        method: 'put',
        headers: {
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({
          email: payload.email,
          password: payload.password,
        }),
      })

      if (resetRequest.status !== 200)
        throw new Error('Resetting user password failed.')
    }),

    logoutUser: thunk(async actions => {
      const token = getCookie('wtwJWT')
      if (token) {
        fetch(`${ENDPOINT}/logout`, {
          method: 'post',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
      }

      actions.clearUser()
    }),
  },
}
